'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_MANUAL_RING_VISIBILITY,
  readManualRingVisibility,
  writeManualRingVisibility,
  type ManualRingVisibility,
} from '@/lib/manual-rings';

/**
 * Whether the calibration rings are painted at rest, remembered between sessions.
 *
 * Read AFTER mount, never during render, for the reason `useManualStageLayout`
 * gives: the server renders without any `localStorage`, so seeding state from it
 * during the first render is a hydration mismatch. The first paint therefore
 * always shows the rings, and a stored preference replaces it on the next tick.
 *
 * Unlike the layout hook there is no `onBeforeChange`. Painting is not scale:
 * toggling this mid-gesture changes nothing about the geometry the vector is
 * normalised to, so there is no drag to cancel and nothing to tell the aircraft.
 */
export function useManualRingVisibility({
  operator = null,
}: {
  /** Stable operator identity (email today). A shared console must not blend two operators' choices. */
  operator?: string | null;
} = {}): {
  visibility: ManualRingVisibility;
  setVisibility: (next: ManualRingVisibility) => void;
  toggle: () => void;
  /** False when the choice could not be persisted, so the caller can say so rather than lie. */
  persisted: boolean;
} {
  const [visibility, setVisibilityState] = useState<ManualRingVisibility>(
    DEFAULT_MANUAL_RING_VISIBILITY,
  );
  const [persisted, setPersisted] = useState(true);

  useEffect(() => {
    setVisibilityState(readManualRingVisibility(operator));
  }, [operator]);

  // No ref for the current value, unlike `useManualStageLayout`: nothing here
  // is read from a long-lived subscription or a pointer handler, so plain
  // dependencies are correct and cheaper to reason about. Re-creating these two
  // callbacks when the choice changes costs nothing — the consumer is one key.
  const setVisibility = useCallback(
    (next: ManualRingVisibility) => {
      if (visibility === next) return;
      setPersisted(writeManualRingVisibility(next, operator));
      setVisibilityState(next);
    },
    [operator, visibility],
  );

  const toggle = useCallback(() => {
    setVisibility(visibility === 'always' ? 'active' : 'always');
  }, [setVisibility, visibility]);

  return { visibility, setVisibility, toggle, persisted };
}
